import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { permissionClientAdminApi } from '@/api/admin/permission-client';
import type { RootState } from '@/store';
import type { IThunkAPI } from '@/store/types';
import type { IPermissionClient, IPermissionClientState } from '../types/types';
import { isFulfilledAction, isPendingAction, isRejectedAction } from '@/helpers';

const initialState: IPermissionClientState = {
  permissionClientList: [],
  isError: false,
  isSuccess: false,
  isFetching: false,
};

export const getPermissionClientList = createAsyncThunk<Array<IPermissionClient>, number, IThunkAPI>(
  'get/permissionClient/gig/gigId/list',
  async (gigId, thunkAPI) => {
    try {
      const response = await permissionClientAdminApi.getPermissionClient(gigId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getPermissionClientByUserIdList = createAsyncThunk<Array<IPermissionClient>, number, IThunkAPI>(
  'get/permissionClient/user/userId/list',
  async (userId, thunkAPI) => {
    try {
      const { data } = await permissionClientAdminApi.getPermissionClientByUserId(userId);
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getPermissionClientByServiceUserIdList = createAsyncThunk<Array<IPermissionClient>, number, IThunkAPI>(
  'get/permissionClient/serviceUser/userId/list',
  async (userId, thunkAPI) => {
    try {
      const { data } = await permissionClientAdminApi.getPermissionClientByServiceUserId(userId);
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const permissionClientAdminSlice = createSlice({
  name: 'permissionClientAdmin',
  initialState,
  reducers: {
    clearPermissionClientList(state) {
      state.permissionClientList = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getPermissionClientList.fulfilled, (state, action) => {
        state.permissionClientList = action.payload;
      })
      .addCase(getPermissionClientByUserIdList.fulfilled, (state, action) => {
        state.permissionClientList = action.payload;
      })
      .addCase(getPermissionClientByServiceUserIdList.fulfilled, (state, action) => {
        state.permissionClientList = action.payload;
      })
      .addMatcher(isFulfilledAction, state => {
        state.isSuccess = true;
        state.isError = false;
        state.isFetching = false;
      })
      .addMatcher(isRejectedAction, state => {
        state.isError = true;
        state.isSuccess = false;
        state.isFetching = false;
      })
      .addMatcher(isPendingAction, state => {
        state.isFetching = true;
      });
  },
});

// actions
export const { clearPermissionClientList } = permissionClientAdminSlice.actions;

// selectors
export const permissionClientList = (state: RootState): IPermissionClientState['permissionClientList'] =>
  state.permissionClientAdmin.permissionClientList;

export default permissionClientAdminSlice.reducer;
